
/*!
 * @pendo/components 3.25.3
 * (c) 2018-present
 */

import 'lodash/isEqual';
import 'just-kebab-case';
import { l as labelsMixin } from './labels-mixin.js';
import { _ as _objectSpread } from './_rollupPluginBabelHelpers-mixin.js';
import 'lodash/kebabCase';
import 'feather-icons';
import PendoIcon from './pendo-icon.js';
import Form from 'element-ui/lib/form';
import FormItem from 'element-ui/lib/form-item';
import Input from 'element-ui/lib/input';
import 'element-ui/lib/input-number';
import 'lodash/debounce';
import PendoInputNumber from './pendo-input-number.js';
import { PendoInputAutowidth } from './pendo-input-autowidth.js';

var script = {
  name: 'PendoEditableContent',
  components: {
    PendoInputNumber,
    ElForm: Form,
    ElFormItem: FormItem,
    ElInput: Input,
    PendoIcon
  },
  directives: {
    PendoInputAutowidth
  },
  mixins: [labelsMixin],
  inheritAttrs: false,
  props: {
    value: {
      type: [String, Number],
      default: ''
    },
    type: {
      type: String,
      default: 'text'
    },
    inputRef: {
      type: String,
      default: 'input'
    },
    inputProps: {
      type: Object,
      default: () => ({})
    },
    exitEvent: {
      type: String,
      default: undefined
    },
    emptyText: {
      type: String,
      default: 'Not Set'
    },
    contentSlotClass: {
      type: String,
      default: null
    },
    contentSlotIcon: {
      type: [Boolean, String],
      default: 'edit-2'
    },
    maxWidth: {
      type: [String, Number],
      default: null
    },
    autoWidth: {
      type: Boolean,
      default: false
    },
    beforeExit: {
      type: Function,
      default: null
    },
    allowEmpty: {
      type: Boolean,
      default: false
    },
    validationRules: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      initialValue: null,
      internalValue: null,
      inEditMode: false,
      isPendingExit: false,
      beforeExitError: null,
      rules: this.validationRules
    };
  },

  computed: {
    model: {
      get() {
        return this.internalValue;
      },

      set(val) {
        this.internalValue = val;
        this.$emit('input', val);
      }

    },

    inputAttrs() {
      return this.$props.inputProps;
    },

    listeners() {
      const exitEvent = this.exitEvent,
            $listeners = this.$listeners,
            $slots = this.$slots,
            $scopedSlots = this.$scopedSlots;
      const mountedHook = $slots.content || $scopedSlots.content;

      if (this.type === 'number' && exitEvent) {
        console.warn("[pendo-editable-content]: exit events are not currently supported with type 'number'");
        return _objectSpread({}, $listeners, mountedHook && {
          'hook:mounted': this.bindContentClass
        });
      }

      return _objectSpread({}, $listeners, exitEvent && {
        [exitEvent]: this.confirm
      }, mountedHook && {
        'hook:mounted': this.bindContentClass
      });
    }

  },
  watch: {
    value(val) {
      this.internalValue = val;
    },

    model() {
      if (this.beforeExitError) {
        this.beforeExitError = null;
      }
    }

  },

  created() {
    this.internalValue = this.value;
  },

  mounted() {
    if (this.$slots.content || this.$scopedSlots.content) {
      this.inputClass = this.getBindingContentClass();
    }

    if (this.inputProps && this.inputProps.autofocus) {
      this.enterEditMode();
    }
  },

  methods: {
    getBindingContentClass() {
      if (!this.contentSlotClass) {
        const contentSlotWrapper = '.pendo-editable-content__slot-content';
        return this.$el.querySelector(contentSlotWrapper).lastElementChild.className;
      }

      return this.contentSlotClass;
    },

    bindContentClass() {
      this.$el.querySelector('.el-input__inner').classList.add(this.inputClass);
    },

    async confirm() {
      if (!this.inEditMode) {
        return;
      }

      try {
        await this.$refs.contentEditableForm.validate();
      } catch (err) {
        return;
      }

      if (this.type === 'text') {
        this.internalValue = this.internalValue.trim();
      }

      if (this.type === 'text' && !this.allowEmpty && this.internalValue.trim().length === 0) {
        return this.cancel();
      }

      this.$emit('confirm', this.internalValue);
      this.startExitProcess();
    },

    cancel() {
      this.model = this.initialValue;
      this.$refs.contentEditableForm.resetFields();
      this.$emit('cancel', this.internalValue);
      this.exitEditMode();
    },

    enterEditMode() {
      this.initialValue = this.internalValue;
      this.inEditMode = true;
      this.$emit('enterEditMode', this.model);
      this.$nextTick(() => {
        let inputRef = this.$refs[this.inputRef];

        if (this.type === 'number') {
          inputRef = inputRef.$refs.elInputNumber;
        }

        if (inputRef) {
          inputRef.focus();
        }
      });
    },

    startExitProcess() {
      if (this.beforeExit && typeof this.beforeExit === 'function') {
        this.isPendingExit = true;
        const before = this.beforeExit(this.internalValue);

        if (before && before.then) {
          return before.then(() => {
            this.exitEditMode();
          }, error => {
            this.isPendingExit = false;
            this.beforeExitError = error;
          });
        }

        if (before !== false) {
          this.exitEditMode();
        } else {
          this.isPendingExit = false;
        }
      } else {
        this.exitEditMode();
      }
    },

    exitEditMode() {
      this.isPendingExit = false;
      this.inEditMode = false;
      this.$emit('exitEditMode', this.internalValue);
    }

  }
};

const __vue_script__ = script;

var __vue_render__ = function () {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('div', {
    staticClass: "pendo-editable-content",
    class: {
      'is-editing': _vm.inEditMode,
      'has-label': !!_vm.topLabel || !!_vm.bottomLabel
    }
  }, [!!_vm.topLabel ? _c('div', {
    staticClass: "pendo-editable-content__label pendo-editable-content__label--top"
  }, [_vm._v("\n        " + _vm._s(_vm.topLabel) + "\n    ")]) : _vm._e(), _vm._v(" "), _c('el-form', {
    ref: "contentEditableForm",
    staticClass: "pendo-editable-content__form",
    attrs: {
      "model": {
        value: _vm.model
      }
    },
    nativeOn: {
      "submit": function ($event) {
        $event.preventDefault();
        return _vm.confirm($event);
      }
    }
  }, [_c('el-form-item', {
    staticClass: "pendo-editable-content__form-item",
    attrs: {
      "show-message": true,
      "rules": _vm.rules,
      "prop": "value"
    }
  }, [_vm.inEditMode ? [_vm.type === 'number' ? [_c('div', {
    staticClass: "pendo-input-group"
  }, [_c('pendo-input-number', _vm._g(_vm._b({
    ref: _vm.inputRef,
    staticClass: "pendo-editable-content__input",
    class: {
      'pendo-editable-content__slot-input': _vm.$slots.content || _vm.$scopedSlots.content
    },
    attrs: {
      "always-show-arrows": "",
      "disabled": _vm.isPendingExit
    },
    nativeOn: {
      "keyup": function ($event) {
        if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "esc", 27, $event.key, ["Esc", "Escape"])) {
          return null;
        }

        return _vm.cancel($event);
      }
    },
    model: {
      value: _vm.model,
      callback: function ($$v) {
        _vm.model = $$v;
      },
      expression: "model"
    }
  }, 'pendo-input-number', _vm.inputAttrs, false), _vm.listeners)), _vm._v(" "), _vm.$slots.append || _vm.$scopedSlots.append ? _c('div', {
    staticClass: "pendo-editable-content__append"
  }, [_vm._t("append", null, {
    "pending": _vm.isPendingExit,
    "confirm": _vm.confirm,
    "cancel": _vm.cancel
  })], 2) : _vm._e()], 1)] : _vm._e(), _vm._v(" "), _vm.type === 'text' ? _c('el-input', _vm._g(_vm._b({
    directives: [{
      name: "pendo-input-autowidth",
      rawName: "v-pendo-input-autowidth",
      value: _vm.autoWidth && {
        maxWidth: _vm.maxWidth,
        extraWidth: 0
      },
      expression: "autoWidth && { maxWidth: maxWidth, extraWidth: 0 }"
    }],
    ref: _vm.inputRef,
    staticClass: "pendo-editable-content__input",
    class: {
      'pendo-editable-content__slot-input': _vm.$slots.content || _vm.$scopedSlots.content,
      'pendo-editable-content__slot-input--with-icon': !!_vm.contentSlotIcon
    },
    attrs: {
      "disabled": _vm.isPendingExit
    },
    nativeOn: {
      "keyup": function ($event) {
        if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "esc", 27, $event.key, ["Esc", "Escape"])) {
          return null;
        }

        return _vm.cancel($event);
      }
    },
    scopedSlots: _vm._u([_vm._l(_vm.$scopedSlots, function (slot, name) {
      return {
        key: name,
        fn: function () {
          return [name !== 'content' ? _vm._t(name, null, {
            "pending": _vm.isPendingExit,
            "confirm": _vm.confirm,
            "cancel": _vm.cancel
          }) : _vm._e()];
        },
        proxy: true
      };
    })], null, true),
    model: {
      value: _vm.model,
      callback: function ($$v) {
        _vm.model = $$v;
      },
      expression: "model"
    }
  }, 'el-input', _vm.inputAttrs, false), _vm.listeners)) : _vm._e(), _vm._v(" "), _vm.beforeExitError ? _c('div', {
    staticClass: "pendo-editable-content__error el-form-item__error"
  }, [_vm._v("\n                    " + _vm._s(_vm.beforeExitError) + "\n                ")]) : _vm._e()] : _vm._e(), _vm._v(" "), !_vm.inEditMode ? [_vm.$slots.content || _vm.$scopedSlots.content ? _c('div', {
    staticClass: "pendo-editable-content__slot",
    on: {
      "mousedown": function ($event) {
        $event.preventDefault();
        $event.stopPropagation();
        return _vm.enterEditMode($event);
      }
    }
  }, [_c('div', {
    staticClass: "pendo-editable-content__slot-content"
  }, [_vm._t("content", null, {
    "enterEditMode": _vm.enterEditMode,
    "pending": _vm.isPendingExit,
    "confirm": _vm.confirm,
    "cancel": _vm.cancel
  })], 2), _vm._v(" "), _vm.contentSlotIcon ? _c('pendo-icon', {
    staticClass: "pendo-editable-content__slot-icon",
    attrs: {
      "type": _vm.contentSlotIcon,
      "size": "14",
      "display": "grid"
    }
  }) : _vm._e()], 1) : _vm._e(), _vm._v(" "), !_vm.$slots.content && !_vm.$scopedSlots.content ? _c('div', {
    staticClass: "pendo-editable-content__text",
    on: {
      "mousedown": function ($event) {
        $event.preventDefault();
        $event.stopPropagation();
        return _vm.enterEditMode($event);
      }
    }
  }, [_c('span', [_vm._t("value", [_vm._v("\n                            " + _vm._s(_vm.internalValue || _vm.emptyText) + "\n                        ")], {
    "value": _vm.internalValue
  })], 2)]) : _vm._e()] : _vm._e()], 2)], 1), _vm._v(" "), !!_vm.bottomLabel ? _c('div', {
    staticClass: "pendo-editable-content__label pendo-editable-content__label--bottom"
  }, [_vm._v("\n        " + _vm._s(_vm.bottomLabel) + "\n    ")]) : _vm._e()], 1);
};

var __vue_staticRenderFns__ = [];
const __vue_inject_styles__ = undefined;
const __vue_scope_id__ = undefined;
const __vue_is_functional_template__ = false;

function __vue_normalize__(template, style, script, scope, functional, moduleIdentifier, createInjector, createInjectorSSR) {
  const component = (typeof script === 'function' ? script.options : script) || {};
  component.__file = "pendo-editable-content.vue";

  if (!component.render) {
    component.render = template.render;
    component.staticRenderFns = template.staticRenderFns;
    component._compiled = true;
    if (functional) component.functional = true;
  }

  component._scopeId = scope;

  return component;
}

var PendoEditableContent = __vue_normalize__({
  render: __vue_render__,
  staticRenderFns: __vue_staticRenderFns__
}, __vue_inject_styles__, __vue_script__, __vue_scope_id__, __vue_is_functional_template__);

export default PendoEditableContent;
